import React, { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';

import {
    NSLabel, NSInputNumberStepper, NSSwitch,
} from 'bricks';
import NSProFormaInput from 'bricks/NSProFormaInput/NSProFormaInput';
import { IOptionAnyValue } from 'bricks/types';
import {
    FORM_ROW_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
    ProFormaAreaMode,
    ProjectActualsDiscrepanciesEnum,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { numeralFormatter } from 'ns_libs/formatter';
import { getProFormaModelDisplayCards } from 'views/ProFormaTable/helpers';
import { IProFormaValues } from 'views/ProFormaTable/types';
import { ProFormaModelIds } from 'constants/proForma';
import ProjectActualsIcon from 'views/ProFormaTable/components/ProjectActualsIcon/ProjectActualsIcon';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import ProFormaSizeParking from './ProFormaSizeParking/ProFormaSizeParking';
import ProFormaSizeAreaFields from './ProFormaSizeAreaFields/ProFormaSizeAreaFields';
import StoryAreaBreakdown from './StoryAreaBreakdown/StoryAreaBreakdown';
import ProFormaSizeOptionalFields from './ProFormaSizeOptionalFields/ProFormaSizeOptionalFields';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';

export interface IProFormaSizeDetailsProps {
    values: IProFormaValues;
    handleInputChange: (name: string, value: string | number) => void
    handleSelect: (name: string, option: string | IOptionAnyValue) => void;
    actualsDiscrepancies?: string[];
}

const ProFormaSizeDetails = ({
    values, handleInputChange, handleSelect, actualsDiscrepancies,
}: IProFormaSizeDetailsProps) => {
    const {
        handleUpdateProForma,
        proForma,
        incomeSummary, shouldUseProjectActuals,
    } = useProFormaSocketContext();

    const [isFar, setIsFar] = useState<boolean>();
    const [floorAreaVaries, setFloorAreaVaries] = useState(proForma.isFloorPlateAreaVaried || false);
    const [storyAreas, setStoryAreas] = useState<(number | null)[]>([]);

    useEffect(() => {
        if (proForma.isFloorPlateAreaVaried !== null) {
            setFloorAreaVaries(proForma.isFloorPlateAreaVaried);
        }
    }, [proForma?.isFloorPlateAreaVaried]);

    useEffect(() => {
        if (isFar === undefined && (proForma?.areaMode !== undefined || proForma?.calculateArea !== undefined)) {
            setIsFar(proForma.areaMode === ProFormaAreaMode.FAR || !proForma.calculateArea);
        }
    }, [proForma?.areaMode, proForma?.calculateArea]);

    useEffect(() => {
        if (proForma?.storyArea?.length) {
            setStoryAreas(proForma.storyArea.map(area => area ?? null));
        } else if (values.averageStories && !Number.isNaN(values.averageStories)) {
            setStoryAreas(Array(Math.round(values.averageStories)).fill(null));
        }
    }, [proForma?.storyArea, values.averageStories]);

    const totalUnits = incomeSummary.totalUnits || 0; // from C2
    const rentableSquareFootage = incomeSummary.totalRentableSf || 0; // from C2
    const displayCards = getProFormaModelDisplayCards(proForma.proFormaModelId, values, totalUnits, rentableSquareFootage, isFar);

    const handleStoryAreaChange = (story: number, area: number | null) => {
        const newAreas = [...storyAreas];
        newAreas[story - 1] = area;
        setStoryAreas(newAreas);
        // Ensure array is in order with 0s for empty positions and only contains numbers
        const orderedAreas: number[] = Array(Math.round(values.averageStories)).fill(0).map((_, index) => {
            const value = newAreas[index];
            return value === null || value === undefined ? 0 : Number(value);
        });
        handleUpdateProForma([{
            key: 'storyArea',
            value: orderedAreas,
        }]);
    };

    return (
        <Row>
            <Col md={12} lg={8}>
                <ProFormaSizeAreaFields
                    values={values}
                    handleInputChange={handleInputChange}
                    handleSelect={handleSelect}
                    isFar={isFar}
                    setIsFar={setIsFar}
                />

                <RenderIf isTrue={proForma.proFormaModelId !== ProFormaModelIds.APARTMENT}>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <NSLabel className={FORM_LABEL_CLASS_NAME}>Buildings</NSLabel>
                        <NSInputNumberStepper
                            name="buildings"
                            value={values.buildings}
                            onChange={(count: number) => {
                                handleUpdateProForma([{
                                    key: 'buildings',
                                    value: String(count),
                                }]);
                                handleInputChange('buildings', count);
                            }}
                            min={1}
                            inputGroupClassName="w-25"
                            step={1}
                        />
                    </div>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <NSLabel className="pl-0 col-4 font-weight-normal text-dark text-nowrap">
                            {values.buildings <= 1 ? 'Stories' : 'Avg. # of stories'}
                        </NSLabel>
                        <div className={floorAreaVaries ? 'd-flex flex-column w-100 p-2 NSCard--level-3' : 'd-flex flex-column w-25'}>
                            <NSInputNumberStepper
                                name="averageStories"
                                value={values.averageStories}
                                onChange={(count: number) => {
                                    handleUpdateProForma([{
                                        key: 'averageStories',
                                        value: String(count),
                                    }]);
                                    handleInputChange('stories', count);
                                }}
                                min={1}
                                inputGroupClassName={floorAreaVaries ? 'w-50' : ''}
                                step={1}
                            />
                            <RenderIf isTrue={proForma.proFormaModelId !== ProFormaModelIds.APARTMENT
                                && values.buildings === 1 && Math.round(values.averageStories) > 1}
                            >
                                <div className="d-flex pt-1 pb-2">
                                    <NSSwitch
                                        name="floorAreaVaries"
                                        id="1"
                                        checked={floorAreaVaries}
                                        containerClassName="pr-1"
                                        onChange={() => {
                                            const newFloorAreaVaries = !floorAreaVaries;
                                            setFloorAreaVaries(newFloorAreaVaries);
                                            handleUpdateProForma([{ key: 'isFloorPlateAreaVaried', value: newFloorAreaVaries }]);
                                        }}
                                    />
                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>
                                        Floor plate area varies
                                    </NSLabel>
                                </div>
                                <StoryAreaBreakdown
                                    storyAreas={storyAreas}
                                    floorAreaVaries={floorAreaVaries}
                                    averageStories={Math.round(values.averageStories)}
                                    onAreaChange={handleStoryAreaChange}
                                />
                            </RenderIf>
                        </div>
                    </div>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <div className="d-flex col-4 pl-0">
                            <RenderIf isTrue={shouldUseProjectActuals
                                && actualsDiscrepancies?.includes(ProjectActualsDiscrepanciesEnum.BUILT_GROSS_SQUARE_FOOTAGE)}
                            >
                                <ProjectActualsIcon
                                    name={ProjectActualsDiscrepanciesEnum.BUILT_GROSS_SQUARE_FOOTAGE}
                                    className="my-auto mr-1"
                                />
                            </RenderIf>
                            <NSLabel className={FORM_LABEL_CLASS_NAME}>Built gross SF</NSLabel>
                        </div>

                        <NSProFormaInput
                            type="number"
                            name="builtGrossSquareFootage"
                            value={values.builtGrossSquareFootage}
                            handleUpdateValue={value => {
                                handleInputChange('builtGrossSquareFootage', value);
                                handleUpdateProForma([{ key: 'builtGrossSquareFootage', value: String(value) }]);
                            }}
                            placeholder="Enter built gross SF"
                            inputGroupClassName="p-0"
                            appendInputAddon={<div className="px-2">ft²</div>}
                        />
                    </div>
                </RenderIf>

                <RenderIf isTrue={proForma.proFormaModelId === ProFormaModelIds.INDUSTRIAL}>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <NSLabel className={FORM_LABEL_CLASS_NAME}>Avg. clear height</NSLabel>
                        <NSProFormaInput
                            type="number"
                            name="averageClearHeight"
                            value={values.averageClearHeight}
                            handleUpdateValue={value => {
                                handleInputChange('averageClearHeight', value);
                                handleUpdateProForma([{ key: 'averageClearHeight', value }]);
                            }}
                            placeholder="Enter avg. clear height"
                            inputGroupClassName="p-0"
                            appendInputAddon={<div className="px-2">ft</div>}
                        />
                    </div>
                </RenderIf>

                <ProFormaSizeParking
                    values={values}
                    handleInputChange={handleInputChange}
                />

                <RenderIf isTrue={proForma.proFormaModelId === ProFormaModelIds.APARTMENT}>
                    <ProFormaSizeOptionalFields
                        values={values}
                        handleInputChange={handleInputChange}
                        floorAreaVaries={floorAreaVaries}
                        setFloorAreaVaries={setFloorAreaVaries}
                        storyAreas={storyAreas}
                        handleStoryAreaChange={handleStoryAreaChange}
                        useSocketContext
                    />
                </RenderIf>

            </Col>
            <Col md={12} lg={4}>
                <Row className="mx-n1">
                    {displayCards.map(({
                        label, value, format, valueSuffix, fullWidth,
                    }) => (
                        <Col className="px-1 mb-2" md={12} lg={fullWidth ? 12 : 6} key={label}>
                            <MetricOutputCard
                                label={label}
                                value={`${numeralFormatter(value, format)} ${valueSuffix || ''}`}
                                showInProgress={!value}
                            />
                        </Col>
                    ))}
                </Row>
            </Col>
        </Row>
    );
};

export default ProFormaSizeDetails;
